import type { TraceErrorObject } from 'owa-analytics-types';
import type { IframeScenario, ScenarioSettings } from '../settings/iframes-mapping';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { iframesMapping } from '../settings/iframes-mapping';
import { setIframeInfo } from '../utils/getIframeInfo';
import { getItem, setItem } from 'owa-local-storage';
import { owaDate, differenceInDays } from 'owa-datetime';

const USER_HEURISTIC_FOR_PRELOADING_KEY = 'userInteractedWithIframeScenarios';
const TWO_WEEKS_IN_DAYS = 14;

// Type for the new date-aware scenario storage format
type ScenarioTimestamps = Record<IframeScenario, string>;

export function useIframeSettings(
    scenario: IframeScenario,
    targetWindow?: Window
): ScenarioSettings {
    const settings = getIframeSettings(scenario, targetWindow);

    if (!settings) {
        const error = new Error('No iframe settings found for scenario');
        (error as TraceErrorObject).additionalInfo = { scenario };
        throw error;
    }

    return checkFeatureFlightsForSettings(scenario, settings, targetWindow);
}

export function getIframeSettings(
    scenario: IframeScenario,
    targetWindow?: Window
): ScenarioSettings {
    return checkFeatureFlightsForSettings(scenario, iframesMapping[scenario], targetWindow);
}

function checkFeatureFlightsForSettings(
    scenario: IframeScenario,
    settings: ScenarioSettings,
    targetWindow?: Window
): ScenarioSettings {
    const preloadingHeuristicResult = checkUserHeuristicForPreloading(scenario);

    setIframeInfo(scenario, 'isHeuristicForPreloadingMet', preloadingHeuristicResult);

    const isPopoutWindow = targetWindow && targetWindow !== window;

    return {
        ...settings,
        enablePreloading:
            settings.enablePreloading &&
            preloadingHeuristicResult &&
            isGlobalFeatureEnabled('fwk-copilot-iframe-preload') &&
            !isPopoutWindow,
        enableCaching:
            settings.enableCaching &&
            isGlobalFeatureEnabled('mon-copilot-bizchat-caching') &&
            !isPopoutWindow,
    };
}

function migrateScenariosList(rawData: string): ScenarioTimestamps | null {
    try {
        const parsed = JSON.parse(rawData);

        // If it's already an object (new format), return as-is
        if (typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed as ScenarioTimestamps;
        }

        // If it's an array (legacy format), migrate to object format
        if (Array.isArray(parsed)) {
            const migrated: ScenarioTimestamps = {} as ScenarioTimestamps;
            const currentDate = new Date().toISOString();

            for (const scenario of parsed) {
                if (typeof scenario === 'string') {
                    migrated[scenario as IframeScenario] = currentDate;
                }
            }

            // Save migrated data back to localStorage
            setItem(window, USER_HEURISTIC_FOR_PRELOADING_KEY, JSON.stringify(migrated));
            return migrated;
        }
    } catch (e) {
        // Invalid JSON or other error - return null for graceful fallback
    }

    return null;
}

export function checkUserHeuristicForPreloading(scenario: IframeScenario): boolean {
    const scenariosList = getItem(window, USER_HEURISTIC_FOR_PRELOADING_KEY);

    if (scenariosList) {
        const scenarios = migrateScenariosList(scenariosList);
        if (scenarios && scenario in scenarios) {
            // Single try/catch: validate type and parse timestamp.
            try {
                const ts = scenarios[scenario];

                if (typeof ts !== 'string') {
                    throw new Error('invalid-timestamp');
                }

                const storedDate = owaDate('UTC', ts);
                return differenceInDays(owaDate(), storedDate) <= TWO_WEEKS_IN_DAYS;
            } catch (e) {
                // On any validation or parse failure, remove the bad entry to avoid
                // future exceptions. Storage write is best-effort — quota/unavailable must not throw.
                try {
                    delete scenarios[scenario];
                    setItem(window, USER_HEURISTIC_FOR_PRELOADING_KEY, JSON.stringify(scenarios));
                } catch (storageError) {
                    // Ignore storage errors; returning false is sufficient to prevent preloading.
                }
                return false;
            }
        }
    }

    return false;
}

export function setUserInteractionWithScenario(scenario: IframeScenario): void {
    const scenariosList = getItem(window, USER_HEURISTIC_FOR_PRELOADING_KEY) ?? '[]';
    const currentTimestamp = new Date().toISOString();

    try {
        const scenarios = migrateScenariosList(scenariosList) ?? ({} as ScenarioTimestamps);

        // Always update timestamp, even if scenario already exists (tracks most recent interaction)
        scenarios[scenario] = currentTimestamp;

        setItem(window, USER_HEURISTIC_FOR_PRELOADING_KEY, JSON.stringify(scenarios));
    } catch (e) {
        // Fallback: create new object with just this scenario
        try {
            const fallbackScenarios: ScenarioTimestamps = {} as ScenarioTimestamps;
            fallbackScenarios[scenario] = currentTimestamp;
            setItem(window, USER_HEURISTIC_FOR_PRELOADING_KEY, JSON.stringify(fallbackScenarios));
        } catch (fallbackError) {
            // If even fallback fails, silently fail to avoid breaking iframe functionality
        }
    }
}
